import { useEffect } from 'react';

/**
 * Per-route SEO meta for the SPA. Sets document.title, meta description,
 * canonical link, and Open Graph tags imperatively — no helmet dependency.
 * Last-mounted wins, which matches route-level usage.
 *
 * Canonical origin comes from VITE_SITE_URL (white-label deploys set their
 * own); falls back to window.location.origin.
 */
export interface SeoProps {
  title: string;
  description: string;
  /** Route path beginning with '/'. Used for canonical + og:url. */
  path: string;
  /** Origin-relative social-share image (og:image / twitter:image). Defaults
   *  to the shared brand card. Pass a per-route image for channel pages. */
  image?: string;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function siteOrigin(): string {
  const configured = import.meta.env.VITE_SITE_URL as string | undefined;
  // Treat an empty/blank VITE_SITE_URL as unset so canonical + og:image stay
  // absolute (fall back to the live origin) instead of collapsing to a path.
  const base = configured && configured.trim() ? configured : window.location.origin;
  return base.replace(/\/$/, '');
}

const DEFAULT_OG_IMAGE = '/og-image.png';

export function applySeo({ title, description, path, image }: SeoProps): void {
  const origin = siteOrigin();
  const url = `${origin}${path}`;
  const imageUrl = `${origin}${image ?? DEFAULT_OG_IMAGE}`;
  document.title = title;
  upsertMeta('name', 'description', description);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:type', 'website');
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:image', imageUrl);
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', imageUrl);
  upsertCanonical(url);
}

/**
 * True while a route's <Seo> owns document.title. BrandProvider checks this so
 * its brand-name title fallback doesn't clobber a page's specific title — child
 * (Seo) effects run before the parent (BrandProvider) effect on mount, so the
 * flag is already set by the time BrandProvider would otherwise overwrite it.
 */
let routeTitleOwned = false;
export function isRouteTitleOwned(): boolean {
  return routeTitleOwned;
}

/** Injects a JSON-LD <script> block. Cleaned up on unmount. */
export function JsonLd({ schema }: { schema: Record<string, unknown> }): null {
  useEffect(() => {
    const el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.textContent = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => {
      document.head.removeChild(el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export function Seo({ title, description, path, image }: SeoProps): null {
  useEffect(() => {
    routeTitleOwned = true;
    applySeo({ title, description, path, image });
    return () => {
      routeTitleOwned = false;
    };
  }, [title, description, path, image]);
  return null;
}
